import { toastr } from 'react-redux-toastr';
import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
// eslint-disable-next-line import/no-unresolved
import { convertNullableDateTimeToUkgUserDateTime } from '@commonClient2/services/ukgDateTime';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import { UPDATE_EMPLOYEE_APP_DATA } from './appData.actions';
import {
    getAllBidProfiles,
    getAllArchivedBidProfiles,
    getAllBidSelectionTemplates,
} from '../../services/api-endpoints';
import { parseAssignedShiftlines } from '../../services/utilities/assignedShiftlineModelMapper/assignedShiftlineModelMapper';
import i18n from '../../../services/i18n';

// eslint-disable-next-line import/prefer-default-export
export const fetchEmployeeAppData = () => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const [
            employeeBidProfilesResponse,
            employeeArchivedBidProfilesResponse,
        ] = await Promise.all([
            getAllBidProfiles(),
            getAllArchivedBidProfiles()
        ]);

        /**
         * @type {EmployeeBidProfile.IEmployeeBidProfile[]}
         */
        const employeeBidProfiles = employeeBidProfilesResponse.data.map((x) => ({
            ...x,
            creationDate: convertNullableDateTimeToUkgUserDateTime(x.creationDate),
            signOffDate: convertNullableDateTimeToUkgUserDateTime(x.signOffDate),
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(x.lastUpdate),
            employeeSubmissionDate: convertNullableDateTimeToUkgUserDateTime(x.employeeSubmissionDate),
            employeeSubmissionApprovalDate: convertNullableDateTimeToUkgUserDateTime(x.employeeSubmissionApprovalDate),
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(x.submissionPeriodStartDate),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(x.submissionPeriodEndDate),
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodStartDate),
            requestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodEndDate),
            employeeSequentialSchedule: {
                ...x.employeeSequentialSchedule,
                employeeSubmissionStartDate: convertNullableDateTimeToUkgUserDateTime(
                    x.employeeSequentialSchedule?.employeeSubmissionStartDate,
                ),
                employeeSubmissionEndDate: convertNullableDateTimeToUkgUserDateTime(
                    x.employeeSequentialSchedule?.employeeSubmissionEndDate,
                ),
            },
        }));

        /**
         * @type {EmployeeBidProfile.IEmployeeBidProfile[]}
         */
        const employeeArchivedBidProfiles = employeeArchivedBidProfilesResponse.data.map((x) => ({
            ...x,
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodStartDate),
            requestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodEndDate),
        }));

        /**
         * @type {EmployeeAssignedShiftline.IEmployeeAssignedShiftline[]}
         */
        const assignedShiftlines = parseAssignedShiftlines(employeeBidProfiles.concat(employeeArchivedBidProfiles));

        dispatch({
            type: UPDATE_EMPLOYEE_APP_DATA,
            payload: {
                bidProfiles: employeeBidProfiles,
                assignedShiftlines,
            },
        });
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingBids'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

// eslint-disable-next-line import/prefer-default-export
export const fetchEmployeeArchiveAppData = () => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const [
            employeeArchivedBidProfilesResponse,
            employeeBidProfilesResponse,
        ] = await Promise.all([
            getAllArchivedBidProfiles(),
            getAllBidProfiles(),
        ]);

        /**
         * @type {EmployeeBidProfile.IEmployeeBidProfile[]}
         */
        const employeeArchivedBidProfiles = employeeArchivedBidProfilesResponse.data.map((x) => ({
            ...x,
            creationDate: convertNullableDateTimeToUkgUserDateTime(x.creationDate),
            signOffDate: convertNullableDateTimeToUkgUserDateTime(x.signOffDate),
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(x.lastUpdate),
            employeeSubmissionDate: convertNullableDateTimeToUkgUserDateTime(x.employeeSubmissionDate),
            employeeSubmissionApprovalDate: convertNullableDateTimeToUkgUserDateTime(x.employeeSubmissionApprovalDate),
            submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(x.submissionPeriodStartDate),
            submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(x.submissionPeriodEndDate),
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodStartDate),
            requestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodEndDate),
        }));
        
        /**
         * @type {EmployeeBidProfile.IEmployeeBidProfile[]}
         */
        const employeeBidProfiles = employeeBidProfilesResponse.data.map((x) => ({
            ...x,
            requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodStartDate),
            requestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(x.requestPeriodEndDate),
        }));

        /**
         * @type {EmployeeAssignedShiftline.IEmployeeAssignedShiftline[]}
         */
        const assignedShiftlines = parseAssignedShiftlines(employeeArchivedBidProfiles.concat(employeeBidProfiles));

        dispatch({
            type: UPDATE_EMPLOYEE_APP_DATA,
            payload: {
                archivedBidProfiles: employeeArchivedBidProfiles,
                assignedShiftlines,
            },
        });
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingArchivedBids'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

// eslint-disable-next-line import/prefer-default-export
export const fetchBidSelectionTemplateAppData = () => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const [
            employeeBidSelectionTemplates,
        ] = await Promise.all([
            getAllBidSelectionTemplates(),
        ]);

        const templates = employeeBidSelectionTemplates.data.map((x) => ({
            ...x,
            creationDate: convertNullableDateTimeToUkgUserDateTime(x.creationDate),
            lastUpdate: convertNullableDateTimeToUkgUserDateTime(x.lastUpdate),
        }));

        dispatch({
            type: UPDATE_EMPLOYEE_APP_DATA,
            payload: {
                selectionTemplates: templates,
            },
        });
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
